import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Fuse from 'fuse.js'
import type { Candidate } from '../api'
import { fetchSceneElements } from '../api'
import CandidateCard from './CandidateCard'
import LoadingView from './LoadingView'
import SearchBar from './SearchBar'

const PAGE_SIZE = 20
const MAX_PAGES = 3  // maximum page blocks kept in DOM at once

interface PageBlock {
  pageNum: number
  items: Candidate[]
}

export default function SceneListPage({ refreshKey }: { refreshKey?: number }) {
  const [blocks,    setBlocks]    = useState<PageBlock[]>([])
  const [topOffset, setTopOffset] = useState(0)
  const [hasMore,   setHasMore]   = useState(true)
  const [loading,   setLoading]   = useState(false)
  const [error,     setError]     = useState<string | null>(null)
  const [query,     setQuery]     = useState('')

  // Stable refs so IntersectionObserver callback always sees current state
  const loadingRef  = useRef(false)
  const hasMoreRef  = useRef(true)
  const nextPageRef = useRef(1)
  const blocksRef   = useRef<PageBlock[]>([])
  const blockElems  = useRef<Map<number, HTMLDivElement | null>>(new Map())
  const bottomRef   = useRef<HTMLDivElement | null>(null)

  // Keep blocksRef in sync after each render
  useEffect(() => { blocksRef.current = blocks }, [blocks])

  const loadNext = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return
    loadingRef.current = true
    setLoading(true)
    const page = nextPageRef.current

    try {
      const data = await fetchSceneElements(page, PAGE_SIZE)
      nextPageRef.current = page + 1
      hasMoreRef.current  = data.has_more
      setHasMore(data.has_more)

      // Measure + evict oldest block before updating React state
      const current = blocksRef.current
      let heightToAdd   = 0
      let evictPageNum  = -1

      if (current.length >= MAX_PAGES) {
        const oldest = current[0]
        evictPageNum = oldest.pageNum
        const el = blockElems.current.get(oldest.pageNum)
        heightToAdd = el?.offsetHeight ?? 0
        blockElems.current.delete(oldest.pageNum)
      }

      setBlocks(prev => {
        const next = [...prev, { pageNum: page, items: data.elements }]
        return evictPageNum >= 0 ? next.filter(b => b.pageNum !== evictPageNum) : next
      })

      if (heightToAdd > 0) {
        setTopOffset(o => o + heightToAdd)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load')
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [])

  // Load first page on mount
  useEffect(() => { loadNext() }, [loadNext])

  // Reset and reload when parent signals stale
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return }
    loadingRef.current = false
    hasMoreRef.current = true
    nextPageRef.current = 1
    blocksRef.current = []
    blockElems.current = new Map()
    setBlocks([])
    setTopOffset(0)
    setHasMore(true)
    setError(null)
    loadNext()
  }, [refreshKey, loadNext]) // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = useMemo(() => {
    const seen = new Set<string>()
    return blocks.flatMap(b => b.items).filter(item => {
      if (seen.has(item.item_id)) return false
      seen.add(item.item_id)
      return true
    })
  }, [blocks])
  const fuse = useMemo(() => new Fuse(allItems, {
    keys: [
      { name: 'label',       weight: 2 },
      { name: 'genre',       weight: 1 },
      { name: 'location',    weight: 1 },
      { name: 'description', weight: 0.5 },
    ],
    threshold: 0.4,
    ignoreLocation: true,
  }), [allItems])

  const visibleItems = useMemo(
    () => query.trim() ? fuse.search(query).map(r => r.item) : null,
    [fuse, query]
  )

  // IntersectionObserver on bottom sentinel
  useEffect(() => {
    const el = bottomRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadNext() },
      { rootMargin: '300px' }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [loadNext])

  return (
    <div
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <SearchBar value={query} onChange={setQuery} placeholder="Search scene…" />

      {/* Spacer for evicted pages above — only relevant when not filtering */}
      {!visibleItems && topOffset > 0 && <div style={{ height: topOffset, flexShrink: 0 }} />}

      {visibleItems
        ? visibleItems.map((item) => (
            <CandidateCard key={item.item_id} candidate={item} />
          ))
        : blocks.map((block) => (
            <div
              key={block.pageNum}
              ref={(el) => blockElems.current.set(block.pageNum, el)}
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              {block.items.map((item) => (
                <CandidateCard key={item.item_id} candidate={item} />
              ))}
            </div>
          ))
      }

      {loading && <LoadingView />}

      {error && (
        <div style={{ padding: '10px 14px', background: '#450a0a', color: '#fca5a5', fontSize: '13px', borderRadius: '8px', border: '1px solid #7f1d1d' }}>
          ⚠ {error}
        </div>
      )}

      {!loading && !hasMore && blocks.length === 0 && !error && (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px', marginTop: '40px', opacity: 0.6 }}>
          No entities in the scene yet.
        </div>
      )}

      {/* Bottom sentinel — triggers next page load */}
      {hasMore && !error && <div ref={bottomRef} style={{ height: 1 }} />}
    </div>
  )
}
